<template>
  <div class="container text-secondary">
    <div class="row mb-3">
      <div class="col-2"/>
      <div class="col">
        <div class="h4">Rummikub</div>
      </div>
      <div class="col-2 text-end pe-2">
        <img src="@/assets/back-48.png" alt="terug" class="img-fluid"
             style="height:32px; cursor:pointer;" @click="$router.push('/')"/>
      </div>
    </div>

    <hr class="divider">

    <div v-if="!gameStarted" class="card-modern" style="text-align:center;">
      <div style="color:#718096; margin-bottom:16px;">
        Score per ronde invoeren.<br>Laagste totaal wint! 🏆
      </div>
      <button class="btn-primary-modern" @click="startGame">Spel starten</button>
    </div>

    <div v-else>
      <!-- Score invoer -->
      <div v-if="scoringStarted" class="card-modern">
        <div class="text-center mb-3">
          <div style="font-weight:700; color:#4a5568;">
            {{ players[currentPlayer].gender === 'female' ? '👩' : '👨' }}
            Score van <strong>{{ players[currentPlayer].name }}</strong>
          </div>
          <div style="font-size:0.85rem; color:#a0aec0; margin-top:4px;">Ronde {{ currentRound }}</div>
        </div>

        <div style="display:flex; align-items:center; justify-content:center; gap:12px; margin-bottom:16px;">
          <button class="score-adj-btn" @click="adjustScore(-5)">−5</button>
          <button class="score-adj-btn" @click="adjustScore(-1)">−</button>
          <div style="font-size:2.5rem; font-weight:800; color:#667eea; min-width:70px; text-align:center;">
            {{ tempScore }}
          </div>
          <button class="score-adj-btn" @click="adjustScore(1)">＋</button>
          <button class="score-adj-btn" @click="adjustScore(5)">＋5</button>
        </div>

        <button class="btn-primary-modern" style="width:100%;" @click="confirmScore">
          Bevestig
        </button>
      </div>

      <!-- Wacht op invoer -->
      <div v-else class="card-modern text-center">
        <div style="font-size:1rem; color:#718096; margin-bottom:12px;">Ronde {{ currentRound }}</div>
        <button class="btn-primary-modern" @click="startScoring">Scores invoeren</button>
      </div>

      <!-- Scorebord -->
      <div class="card-modern">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="h6 mb-0">Scorebord</div>
          <button class="edit-toggle" @click="editModus = !editModus">
            <img src="@/assets/edit-48.png" alt="bewerken" style="height:22px;"/>
            {{ editModus ? 'Klaar' : 'Wijzig' }}
          </button>
        </div>

        <div v-if="editModus" style="font-size:0.8rem; color:#a0aec0; margin-bottom:8px;">
          Tik op een score om te wijzigen
        </div>

        <div class="scorebord-wrapper">
          <table class="sticky-table">
            <thead>
            <tr>
              <th class="col-naam sticky-links">Speler</th>
              <th v-for="r in currentRound - 1" :key="r" class="col-ronde">R{{ r }}</th>
              <th class="col-totaal sticky-rechts">Tot</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(player, pi) in players" :key="player.id"
                :class="{ 'winner-row': currentRound > 1 && playerTotal(pi) === lowestScore }">
              <td class="col-naam sticky-links">
                {{ player.name }}
                <span v-if="currentRound > 1 && playerTotal(pi) === lowestScore">🏆</span>
              </td>
              <td v-for="r in currentRound - 1" :key="r"
                  class="col-ronde"
                  :class="{ 'edit-cel': editModus }"
                  @click="editModus ? openEdit(pi, r-1) : null">
                {{ scores[pi]?.[r - 1] ?? '' }}
              </td>
              <td class="col-totaal sticky-rechts">{{ playerTotal(pi) }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Edit popup -->
    <div v-if="editPopup" class="popup-overlay" @click.self="editPopup = false">
      <div class="popup-box">
        <div style="font-weight:700; color:#4a5568; margin-bottom:12px; text-align:center;">
          Score wijzigen: <strong>{{ players[editSpeler].name }}</strong> · R{{ editRonde + 1 }}
        </div>

        <div style="display:flex; align-items:center; justify-content:center; gap:12px; margin-bottom:16px;">
          <button class="score-adj-btn" @click="editScore(-5)">−5</button>
          <button class="score-adj-btn" @click="editScore(-1)">−</button>
          <div style="font-size:2.5rem; font-weight:800; color:#667eea; min-width:70px; text-align:center;">
            {{ editWaarde }}
          </div>
          <button class="score-adj-btn" @click="editScore(1)">＋</button>
          <button class="score-adj-btn" @click="editScore(5)">＋5</button>
        </div>

        <button class="btn-primary-modern" style="width:100%; margin-bottom:8px;" @click="slaEditOp">
          Opslaan
        </button>
        <button class="btn-secondary-modern" style="width:100%;" @click="editPopup = false">
          Annuleren
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import store from "@/store";

export default {
  name: "RummikubGame",
  data() {
    return {
      gameStarted: false,
      scoringStarted: false,
      currentRound: 1,
      currentPlayer: 0,
      tempScore: 0,
      scores: [],
      editModus: false,
      editPopup: false,
      editSpeler: null,
      editRonde: null,
      editWaarde: 0,
    };
  },
  computed: {
    players() {
      return store.state.playersRoom.length >= 2
          ? store.state.playersRoom
          : store.state.players;
    },
    lowestScore() {
      if (this.currentRound <= 1) return null;
      return Math.min(...this.players.map((_, pi) => this.playerTotal(pi)));
    },
  },
  methods: {
    startGame() {
      this.scores = this.players.map(() => []);
      this.currentRound = 1;
      this.gameStarted = true;
    },
    startScoring() {
      this.currentPlayer = 0;
      this.tempScore = 0;
      this.scoringStarted = true;
    },
    adjustScore(delta) {
      this.tempScore = Math.max(0, this.tempScore + delta);
    },
    confirmScore() {
      this.scores[this.currentPlayer].push(this.tempScore);
      if (this.currentPlayer < this.players.length - 1) {
        this.currentPlayer++;
        this.tempScore = 0;
      } else {
        this.scoringStarted = false;
        this.currentPlayer = 0;
        this.currentRound++;
      }
    },
    playerTotal(pi) {
      return (this.scores[pi] || []).reduce((s, v) => s + v, 0);
    },
    openEdit(pi, rondeIndex) {
      this.editSpeler = pi;
      this.editRonde = rondeIndex;
      this.editWaarde = this.scores[pi]?.[rondeIndex] ?? 0;
      this.editPopup = true;
    },
    editScore(delta) {
      this.editWaarde = Math.max(0, this.editWaarde + delta);
    },
    slaEditOp() {
      this.scores[this.editSpeler][this.editRonde] = this.editWaarde;
      this.editPopup = false;
    },
  },
}
</script>

<style scoped>
.score-adj-btn {
  background: #f7fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}
.score-adj-btn:hover { border-color: #667eea; background: #ebf4ff; color: #667eea; }

.scorebord-wrapper {
  overflow-x: auto;
  border-radius: 8px;
}

.sticky-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;
}

.sticky-table th,
.sticky-table td {
  padding: 6px 8px;
  text-align: center;
  border-bottom: 1px solid #f0f4f8;
  white-space: nowrap;
  background: white;
}

.sticky-table thead th {
  color: #718096;
  font-weight: 700;
  border-bottom: 2px solid #e2e8f0;
}

.sticky-table tr:last-child td {
  border-bottom: none;
}

.col-naam {
  text-align: left !important;
  font-weight: 600;
  color: #2d3748;
  min-width: 80px;
}

.sticky-links {
  position: sticky;
  left: 0;
  z-index: 2;
  box-shadow: 2px 0 4px rgba(0,0,0,0.06);
}

.col-totaal {
  font-weight: 800 !important;
  color: #667eea !important;
  min-width: 44px;
}

.sticky-rechts {
  position: sticky;
  right: 0;
  z-index: 2;
  box-shadow: -2px 0 4px rgba(0,0,0,0.06);
}

.col-ronde {
  min-width: 36px;
}

.winner-row td {
  background: #fffff0 !important;
  font-weight: 700;
}

.edit-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.85rem;
  cursor: pointer;
  color: #4a5568;
  transition: all 0.15s;
}
.edit-toggle:hover { border-color: #667eea; color: #667eea; }
.edit-cel { cursor: pointer; }
.edit-cel:hover { background: #ebf4ff !important; border-radius: 4px; }

.btn-secondary-modern {
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  border-radius: 12px;
  padding: 10px 24px;
  font-size: 1rem;
  cursor: pointer;
}

.popup-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.popup-box {
  background: white;
  border-radius: 16px;
  padding: 20px;
  width: 90%;
  max-width: 360px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
}
</style>